import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'

type BadgeStatus = 'active' | 'draft' | 'archived' | 'pending' | 'processing' | 'completed' | 'refunded' | 'shipping'

// Colour only. The WORD each status shows lives in the dictionary — keyed by
// the same union, so a new status is a compile error in all five tables rather
// than an English badge on a Korean screen.
const STATUS_CONFIG: Record<BadgeStatus, { dot: string; bg: string; text: string }> = {
  active:     { dot: 'bg-sky-500',     bg: 'bg-sky-500/10',     text: 'text-sky-700'     },
  draft:      { dot: 'bg-gray-400',    bg: 'bg-gray-400/10',    text: 'text-gray-600'    },
  archived:   { dot: 'bg-gray-400',    bg: 'bg-gray-400/10',    text: 'text-gray-500'    },
  pending:    { dot: 'bg-amber-500',   bg: 'bg-amber-500/10',   text: 'text-amber-700'   },
  processing: { dot: 'bg-blue-500',    bg: 'bg-blue-500/10',    text: 'text-blue-700'    },
  completed:  { dot: 'bg-violet-500',  bg: 'bg-violet-500/10',  text: 'text-violet-700'  },
  refunded:   { dot: 'bg-red-500',     bg: 'bg-red-500/10',     text: 'text-red-700'     },
  shipping:   { dot: 'bg-blue-500',    bg: 'bg-blue-500/10',    text: 'text-blue-700'    },
}

interface StatusBadgeProps {
  status: BadgeStatus
  label?: string
  size?: 'sm' | 'md'
  className?: string
}

export function StatusBadge({ status, label, size = 'sm', className }: StatusBadgeProps) {
  const { strings } = useStudioLocale()
  const config = STATUS_CONFIG[status]
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full font-medium', size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm', config.bg, config.text, className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
      {label ?? strings.businessScreens.review.status[status]}
    </span>
  )
}
